import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import MainCard from "../../components/cards/MainCard";
import { getCardColor } from "../../utils/colorUtils";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGetTenancyBasicsQuery, useGetTenancyByIdQuery } from "../../store/services/tenancy.service";
import { useGetTeamsCoursesQuery } from "../../store/services/university-service";
import KeyboardArrowLeftTwoToneIcon from "@mui/icons-material/KeyboardArrowLeftTwoTone";
import processTeamsTemplate from "../../components/TeamsIntegration/ProcessTeamsTemplate";
import { PRESET_TEAMS_TEMPLATE } from "../../themes/PresetTeamsTemplate";
import ColorPicker from "../../components/ColorPicker";
import { APP_URL } from "../../config";

function TeamsIntegrationEditor() {
    const navigate = useNavigate();
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";
    const { tenancyId } = useSelector((state) => state.tenancy);
    const { user } = useSelector((state) => state.auth);
    const userEmail = user?.email;

    const [isSaving, setIsSaving] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");

    const { data: tenancyData, isLoading: tenancyLoading } = useGetTenancyByIdQuery({ tenancyId });
    const { data: tenancyBasics, isLoading: tenancyBasicsLoading } = useGetTenancyBasicsQuery({ tenancyId });
    const { data: teamsCoursesData, isLoading: coursesLoading } = useGetTeamsCoursesQuery(
        { userId: userEmail },
        { skip: !userEmail }
    );

    const [showStyleEditor, setShowStyleEditor] = useState(false);
    const [primaryColor, setPrimaryColor] = useState(theme.palette.primary.main);
    const [logoSize, setLogoSize] = useState(48);
    const [logoWhiteBackground, setLogoWhiteBackground] = useState(false);

    const [formData, setFormData] = useState({
        headerTitle: "Training Portal",
        headerSubtitle: "Security Awareness Training",
        greetingMessage: "You have pending training courses to complete.",
        sectionTitle: "Your Courses",
        footerText: "Powered by Boxphish Security Awareness",
    });

    // Transform courses from API to preview format (only LMS courses)
    const coursesArray = Array.isArray(teamsCoursesData) ? teamsCoursesData : teamsCoursesData?.courses || [];
    const courses = coursesArray
        .filter(course => course.isLms === true)
        .map(course => ({
            id: course.id || course.universityCourseId,
            name: course.universityCourseName || course.name,
            dateAdded: course.dateCreated ? new Date(course.dateCreated).toLocaleDateString('en-GB') : '',
            coursePath: course.coursePath,
            universityCourseId: course.universityCourseId,
            emailAddress: userEmail,
            isLms: course.isLms
        }));

    // Get logo from either tenancyData or tenancyBasics
    const logoPath = tenancyData?.logo || tenancyBasics?.logo || null;

    const updatePreview = useCallback(() => {
        const previewData = {
            colors: {
                primary: primaryColor
            },
            logo: logoPath,
            logoSize: logoSize,
            logoWhiteBackground: logoWhiteBackground,
            companyName: tenancyData?.name || 'Your Company',
            headerTitle: formData.headerTitle,
            headerSubtitle: formData.headerSubtitle,
            userName: user?.firstName || user?.name || 'User',
            greetingMessage: formData.greetingMessage,
            sectionTitle: formData.sectionTitle,
            footerText: formData.footerText,
            pageTitle: 'Your Training Courses',
            courses: courses
        };

        const processed = processTeamsTemplate(PRESET_TEAMS_TEMPLATE, previewData);
        setPreviewHtml(processed);
    }, [formData, logoPath, tenancyData, primaryColor, logoSize, logoWhiteBackground, courses, user]);

    useEffect(() => {
        updatePreview();
    }, [updatePreview]);

    const handleInputChange = (field) => (event) => {
        setFormData((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // TODO: Save to API/database when backend is ready

            // Simulate save delay
            await new Promise(resolve => setTimeout(resolve, 500));

            navigate(-1);
        } catch (error) {
            console.error("Failed to save settings:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const isLoading = tenancyLoading || tenancyBasicsLoading || coursesLoading;

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ flexGrow: 1, display: "flex" }}>
                <Grid container spacing={2}>
                    {/* Left side - Editor */}
                    <Grid item xs={12} md={4} lg={4}>
                        <Typography variant="h2" sx={{ ml: 2, mb: 2 }}>
                            {showStyleEditor ? "Edit Style" : "Edit Teams Integration"}
                        </Typography>

                        {showStyleEditor ? (
                            <MainCard
                                content={true}
                                boxShadow={true}
                                border={false}
                                sx={{
                                    bgcolor: getCardColor(lightMode),
                                    display: "flex",
                                    flexDirection: "column",
                                    ml: 2,
                                    mb: 1,
                                }}
                            >
                                <Box sx={{ mt: 2 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                                    >
                                        Logo Settings
                                    </Typography>
                                    <Box
                                        sx={{
                                            flexGrow: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 2,
                                        }}
                                    >
                                        {logoPath ? (
                                            <>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        my: 3,
                                                        backgroundColor: logoWhiteBackground ? '#ffffff' : 'transparent',
                                                        padding: logoWhiteBackground ? 2 : 0,
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <img
                                                        src={`${APP_URL}/${logoPath}`}
                                                        alt="Logo"
                                                        width={logoSize}
                                                        style={{ objectFit: "contain" }}
                                                    />
                                                </Box>
                                                <TextField
                                                    label="Logo Width (px)"
                                                    type="number"
                                                    value={logoSize}
                                                    onChange={(e) => setLogoSize(parseInt(e.target.value) || 48)}
                                                    fullWidth
                                                    InputProps={{
                                                        inputProps: { min: 0 },
                                                    }}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={logoWhiteBackground}
                                                            onChange={(e) => setLogoWhiteBackground(e.target.checked)}
                                                        />
                                                    }
                                                    label="White background on logo"
                                                />
                                            </>
                                        ) : (
                                            <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                                                <Typography color="text.secondary">No Logo Selected</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                                    >
                                        Primary Colour
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            gap: 2,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <ColorPicker
                                            color={primaryColor}
                                            onChange={(color) => setPrimaryColor(color)}
                                            disabled={false}
                                        />
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexDirection: "column",
                                        gap: 2,
                                        mt: 3,
                                        mb: 2,
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => setShowStyleEditor(false)}
                                    >
                                        Back to Text Editor
                                    </Button>
                                </Box>
                            </MainCard>
                        ) : (
                            <>
                                <MainCard
                                    content={true}
                                    boxShadow={true}
                                    border={false}
                                    sx={{
                                        bgcolor: getCardColor(lightMode),
                                        display: "flex",
                                        flexDirection: "column",
                                        ml: 2,
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                                    >
                                        Header Settings
                                    </Typography>

                                    <TextField
                                        label="Header Title"
                                        value={formData.headerTitle}
                                        onChange={handleInputChange("headerTitle")}
                                        fullWidth
                                        sx={{ mb: 2 }}
                                    />

                                    <TextField
                                        label="Header Subtitle"
                                        value={formData.headerSubtitle}
                                        onChange={handleInputChange("headerSubtitle")}
                                        fullWidth
                                        sx={{ mb: 2 }}
                                    />
                                </MainCard>

                                <MainCard
                                    content={true}
                                    boxShadow={true}
                                    border={false}
                                    sx={{
                                        bgcolor: getCardColor(lightMode),
                                        display: "flex",
                                        flexDirection: "column",
                                        ml: 2,
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                                    >
                                        Content Settings
                                    </Typography>

                                    <TextField
                                        label="Greeting Message"
                                        value={formData.greetingMessage}
                                        onChange={handleInputChange("greetingMessage")}
                                        fullWidth
                                        multiline
                                        rows={2}
                                        sx={{ mb: 2 }}
                                    />

                                    <TextField
                                        label="Section Title"
                                        value={formData.sectionTitle}
                                        onChange={handleInputChange("sectionTitle")}
                                        fullWidth
                                        sx={{ mb: 2 }}
                                    />

                                    <TextField
                                        label="Footer Text"
                                        value={formData.footerText}
                                        onChange={handleInputChange("footerText")}
                                        fullWidth
                                        sx={{ mb: 2 }}
                                    />

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => setShowStyleEditor(true)}
                                        sx={{ mt: 1 }}
                                    >
                                        Edit Style
                                    </Button>
                                </MainCard>
                            </>
                        )}
                    </Grid>

                    {/* Right side - Preview */}
                    <Grid item xs={12} md={8} lg={8}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                            }}
                        >
                            <Typography variant="h2">Preview</Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<KeyboardArrowLeftTwoToneIcon />}
                                    onClick={() => navigate(-1)}
                                    sx={{ whiteSpace: "nowrap" }}
                                >
                                    Back
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSave}
                                    sx={{
                                        bgcolor: theme.palette.secondary.main,
                                        "&:hover": {
                                            bgcolor: theme.palette.secondary.dark,
                                        },
                                        whiteSpace: "nowrap"
                                    }}
                                    disabled={isSaving}
                                >
                                    {isSaving ? <CircularProgress size={24} /> : "Save & Apply"}
                                </Button>
                            </Box>
                        </Box>
                        <MainCard
                            content={true}
                            boxShadow={true}
                            border={false}
                            sx={{
                                bgcolor: getCardColor(lightMode),
                                display: "flex",
                                flexDirection: "column",
                                mb: 2,
                                mr: 2,
                                p: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 'calc(90vh - 200px)',
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    backgroundColor: '#f5f5f5'
                                }}
                            >
                                <iframe
                                    srcDoc={previewHtml}
                                    style={{
                                        border: 'none',
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: '#f5f5f5'
                                    }}
                                    title="Teams Integration Preview"
                                    sandbox="allow-same-origin allow-scripts allow-top-navigation-by-user-activation allow-popups allow-popups-to-escape-sandbox"
                                />
                            </Box>
                        </MainCard>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}

export default TeamsIntegrationEditor;
